import { connectorDetailPath } from "../domain/connectorNavigation";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { deliveryPointDisplayName } from "../domain/deliveryPointPresentation";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  CloudSun,
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  CircleUserRound,
  ClipboardCheck,
  Cpu,
  Database,
  Eye,
  EyeOff,
  FileBarChart,
  FileSignature,
  Gauge,
  LogOut,
  MapPinned,
  Mail,
  Menu,
  Network,
  PanelLeftClose,
  PanelLeftOpen,
  ReceiptText,
  Settings,
  SwatchBook,
  Sparkles,
  Users,
  Wrench,
  X,
} from "lucide-react";
import type {
  DeliveryPoint,
  Notification,
  Role,
  SearchResult,
} from "@syllarys/contracts";
import { BrandLoadingScreen } from "./BrandLoadingScreen";
import { apiClient, useEms } from "../state/EmsContext";
import { useBrand } from "../state/BrandContext";
import {
  selectCompany,
  selectPoint,
  selectSite,
  usePortfolio,
} from "../hooks/usePortfolio";
import { GlobalSearch } from "./GlobalSearch";
import { ContextHelp } from "./ContextHelp";
import { NotificationPreferences } from "./NotificationPreferences";
import { CopyValueButton } from "./ui";
import { safeInternalReturnTo, scopedPath } from "../utils/navigationContext";

interface NavItem {
  to: string;
  label: string;
  icon: typeof Gauge;
  roles?: Role[];
  activePathPrefix?: string;
  activeSearch?: Record<string, string>;
}

interface NavGroup {
  id: string;
  label: string;
  icon: typeof Gauge;
  roles?: Role[];
  children: NavItem[];
}

const mainNav: NavItem[] = [
  {
    to: "/app/portfolio",
    label: "Portefeuille",
    icon: Building2,
    roles: ["manager", "client"],
  },
  {
    to: "/app/map",
    label: "Carte",
    icon: MapPinned,
    roles: ["manager", "client"],
  },
  {
    to: "/app/dashboard",
    label: "Tableau de bord",
    icon: Gauge,
    roles: ["manager", "client"],
  },
  {
    to: "/app/analysis",
    label: "Analyses",
    icon: BarChart3,
    roles: ["manager", "client"],
    activePathPrefix: "/app/analysis/",
  },
  {
    to: "/app/workflow/anomalies",
    label: "Anomalies",
    icon: Activity,
    roles: ["manager"],
  },
  {
    to: "/app/workflow/recommendations",
    label: "Recommandations",
    icon: Sparkles,
    roles: ["manager", "client"],
  },
  {
    to: "/app/workflow/invoices",
    label: "Factures",
    icon: ReceiptText,
    roles: ["manager", "client"],
  },
  {
    to: "/app/workflow/cpe",
    label: "CPE",
    icon: ClipboardCheck,
    roles: ["manager", "client"],
  },
  {
    to: "/app/workflow/works",
    label: "Travaux",
    icon: Wrench,
    roles: ["manager", "client"],
  },
  {
    to: "/app/workflow/reports",
    label: "Rapports",
    icon: FileBarChart,
    roles: ["manager", "client"],
  },
  {
    to: "/app/site/weather",
    label: "Météo",
    icon: CloudSun,
    roles: ["manager", "client"],
  },
];

const adminNav: (NavItem | NavGroup)[] = [
  {
    to: "/app/mandates",
    label: "Mandats",
    icon: FileSignature,
    roles: ["manager"],
  },
  {
    to: "/app/admin/portfolio",
    label: "Entreprises & sites",
    icon: Database,
    roles: ["manager"],
  },
  {
    to: "/app/site/characteristics",
    label: "Structure du site",
    icon: Network,
    roles: ["manager"],
    activePathPrefix: "/app/site/",
  },
  {
    to: "/app/admin/users",
    label: "Utilisateurs",
    icon: Users,
    roles: ["manager"],
  },
  {
    to: "/app/admin/brands",
    label: "Marques",
    icon: SwatchBook,
    roles: ["super_admin"],
  },
  {
    to: "/app/admin/users",
    label: "Utilisateurs",
    icon: Users,
    roles: ["super_admin"],
  },
  {
    id: "connector-navigation",
    label: "Connecteurs",
    icon: Settings,
    roles: ["super_admin"],
    children: [
      {
        to: "/app/admin/connectors",
        label: "Vue d’ensemble",
        icon: Settings,
        activeSearch: { connector: "" },
      },
      {
        to: "/app/admin/gtb",
        label: "Rattachement GTB WIT",
        icon: Network,
      },
    ],
  },
  {
    id: "data-navigation",
    label: "Données",
    icon: Database,
    roles: ["super_admin"],
    children: [
      {
        to: "/app/admin/weather-data",
        label: "Météo-France",
        icon: CloudSun,
        roles: ["super_admin"],
      },
      {
        to: "/app/admin/enedis-data",
        label: "Enedis",
        icon: Activity,
        roles: ["super_admin"],
      },
      {
        to: "/app/admin/grdf-data",
        label: "GRDF",
        icon: Activity,
        roles: ["super_admin"],
      },
      {
        to: "/app/admin/gtb-data",
        label: "GTB WIT",
        icon: Activity,
        roles: ["super_admin"],
      },
    ],
  },
  {
    to: "/app/admin/emails",
    label: "E-mails",
    icon: Mail,
    roles: ["super_admin"],
  },
  {
    to: "/app/admin/audit",
    label: "Journal",
    icon: BookOpen,
    roles: ["super_admin", "manager", "client"],
  },
];

const DESKTOP_NAV_KEY = "syllarys-ems-ui:v1:desktop-navigation";
const LEGACY_DESKTOP_NAV_KEY = "phebus-ems-ui:v2:desktop-navigation";

/** Rend un PDL distinguable dans le sélecteur, même si son libellé est générique. */
export function deliveryPointContextLabel(point: DeliveryPoint): string {
  return deliveryPointDisplayName(point);
}

function desktopNavigationStartsExpanded(): boolean {
  try {
    const stored = window.localStorage.getItem(DESKTOP_NAV_KEY);
    if (stored) return stored !== "collapsed";
    const legacy = window.localStorage.getItem(LEGACY_DESKTOP_NAV_KEY);
    if (legacy) {
      window.localStorage.setItem(DESKTOP_NAV_KEY, legacy);
      window.localStorage.removeItem(LEGACY_DESKTOP_NAV_KEY);
      return legacy !== "collapsed";
    }
    return true;
  } catch {
    return true;
  }
}

function allowed(item: { roles?: Role[] }, role: Role): boolean {
  return !item.roles || item.roles.includes(role);
}

function navItemIsActive(
  item: NavItem,
  currentPath: string,
  routerMatch: boolean,
  currentSearch = "",
): boolean {
  if (item.activeSearch) {
    const params = new URLSearchParams(currentSearch);
    return (
      currentPath === item.to.split("?")[0] &&
      Object.entries(item.activeSearch).every(
        ([name, value]) => (params.get(name) ?? "") === value,
      )
    );
  }
  return (
    routerMatch ||
    Boolean(
      item.activePathPrefix && currentPath.startsWith(item.activePathPrefix),
    )
  );
}

export function AppShell({ children }: { children: ReactNode }): ReactNode {
  const {
    db,
    session,
    role,
    previewRole,
    setPreviewRole,
    logout,
    switchBrand,
  } = useEms();
  const {
    companies,
    sites,
    deliveryPoints,
    selectedCompany,
    selectedSite,
    selectedPoint,
  } = usePortfolio();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [brandSwitching, setBrandSwitching] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const contextTrigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!contextOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setContextOpen(false);
      contextTrigger.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [contextOpen]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [railLabel, setRailLabel] = useState<{
    label: string;
    top: number;
  } | null>(null);
  const [desktopNavExpanded, setDesktopNavExpanded] = useState(
    desktopNavigationStartsExpanded,
  );
  const [navigationGroups, setNavigationGroups] = useState<
    Record<string, boolean>
  >({});
  const navigate = useNavigate();
  const { brand } = useBrand();
  const location = useLocation();
  const connectorsQuery = useQuery({
    queryKey: ["platform-connectors"],
    queryFn: () => apiClient.listConnectors(),
    enabled: role === "super_admin",
    staleTime: 60_000,
  });
  const notificationsQuery = useQuery({
    queryKey: ["notifications", session?.brand.id, session?.user.id],
    queryFn: () => apiClient.listNotifications(session?.brand.id ?? ""),
    enabled: Boolean(session),
    refetchInterval: 60_000,
  });
  const notifications = notificationsQuery.data ?? [];
  const unreadNotifications = notifications.filter(
    (notification) => notification.readAt === null,
  );
  async function updateNotification(
    notification: Notification,
    snooze = false,
  ): Promise<void> {
    if (!session) return;
    try {
      await apiClient.readNotification(
        session.brand.id,
        notification.id,
        snooze
          ? { snoozedUntil: new Date(Date.now() + 3_600_000).toISOString() }
          : { read: notification.readAt === null },
      );
      await notificationsQuery.refetch();
    } catch {
      setNotificationError(
        "Impossible de mettre à jour cette notification. Actualisez la liste.",
      );
    }
  }

  async function openNotification(notification: Notification): Promise<void> {
    if (!session) return;
    setNotificationError("");
    try {
      if (notification.readAt === null) {
        await apiClient.readNotification(session.brand.id, notification.id);
        await notificationsQuery.refetch();
      }
      setNotificationsOpen(false);
      await navigate(notification.path);
    } catch {
      setNotificationError(
        "Cette notification n’est plus accessible ou le réseau est indisponible. Actualisez la liste.",
      );
      void notificationsQuery.refetch();
    }
  }

  useEffect(() => {
    const parameters = new URLSearchParams(location.search);
    const companyId = parameters.get("company");
    const siteId = parameters.get("site");
    const pointId = parameters.get("point");
    if (!companyId && !siteId && !pointId) {
      if (parameters.get("context") === "1" && selectedCompany)
        selectCompany("*");
      return;
    }
    const point = pointId
      ? deliveryPoints.find((item) => item.id === pointId)
      : undefined;
    const site = point
      ? sites.find((item) => item.id === point.siteId)
      : siteId
        ? sites.find((item) => item.id === siteId)
        : undefined;
    const company = site
      ? companies.find((item) => item.id === site.companyId)
      : companyId
        ? companies.find((item) => item.id === companyId)
        : undefined;
    const coherent =
      (!pointId || Boolean(point)) &&
      (!siteId || site?.id === siteId) &&
      (!companyId || company?.id === companyId);
    if (!coherent || (pointId && !point) || (siteId && !site)) {
      selectCompany("*");
      parameters.delete("company");
      parameters.delete("site");
      parameters.delete("point");
      parameters.delete("context");
      parameters.set("scopeError", "inaccessible");
      void navigate(
        `${location.pathname}${parameters.size ? `?${parameters.toString()}` : ""}`,
        { replace: true },
      );
      return;
    }
    if (point) {
      if (selectedPoint?.id !== point.id) selectPoint(point.id);
    } else if (site) {
      if (selectedSite?.id !== site.id || selectedPoint) selectSite(site.id);
    } else if (
      company &&
      (selectedCompany?.id !== company.id || selectedSite || selectedPoint)
    )
      selectCompany(company.id);
  }, [
    companies,
    deliveryPoints,
    location.pathname,
    location.search,
    navigate,
    selectedCompany,
    selectedPoint,
    selectedSite,
    sites,
  ]);
  const scopeMessage =
    new URLSearchParams(location.search).get("scopeError") === "inaccessible"
      ? "Le périmètre du lien n’est plus accessible. Aucun autre Site n’a été sélectionné à sa place."
      : "";

  // Bandeau « Jeu de démonstration » seulement quand c'est vrai : aucune
  // série réelle dans la marque active. Sinon, chaque écran qualifie ses
  // propres données (badge sur la série, provenance de l'analyse).
  const onlySyntheticSeries = useMemo(
    () => db.series.length > 0 && db.series.every((series) => series.synthetic),
    [db.series],
  );

  const visibleMain = useMemo(
    () =>
      role
        ? mainNav.filter(
            (item) =>
              allowed(item, role) &&
              (item.to !== "/app/map" || role !== "client" || sites.length > 1),
          )
        : [],
    [role, sites.length],
  );
  const visibleAdmin = useMemo(
    () =>
      role
        ? adminNav
            .filter((item) => allowed(item, role))
            .map((item) => {
              if (!("children" in item) || item.id !== "connector-navigation")
                return item;
              return {
                ...item,
                children: [
                  ...item.children.slice(0, 1),
                  ...[...(connectorsQuery.data ?? [])]
                    .sort(
                      (left, right) =>
                        Number(right.category === "weather") -
                        Number(left.category === "weather"),
                    )
                    .map((connector): NavItem => ({
                      to: connectorDetailPath(connector.id),
                      label:
                        connector.provider === "meteo-france"
                          ? "Météo-France"
                          : connector.name,
                      icon:
                        connector.category === "weather"
                          ? CloudSun
                          : connector.category === "gtb"
                            ? Cpu
                            : Activity,
                    })),
                  ...item.children.slice(1),
                ],
              };
            })
        : [],
    [role, connectorsQuery.data],
  );
  const accessibleBrands = useMemo(
    () =>
      session
        ? session.user.platformRole === "super_admin"
          ? db.brands
              .filter((brand) => brand.status === "active")
              .map((brand) => ({ membership: session.activeMembership, brand }))
          : session.user.memberships.flatMap((membership) => {
              const brand = db.brands.find(
                (item) => item.id === membership.brandId,
              );
              return brand?.status === "active" ? [{ membership, brand }] : [];
            })
        : [],
    [db.brands, session],
  );
  if (!session || !role) return null;
  if (brandSwitching) return <BrandLoadingScreen />;

  async function handleLogout(): Promise<void> {
    await logout();
    void navigate("/login");
  }

  // Aperçu Energy Manager : l'interface se présente comme pour ce rôle, la
  // session reste celle du super-admin (aucune identité empruntée).
  function enterPreview(): void {
    setPreviewRole("manager");
    setProfileOpen(false);
    void navigate("/app/portfolio");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  function leavePreview(): void {
    setPreviewRole(null);
    setProfileOpen(false);
    void navigate("/app/admin/audit");
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  function showRailLabel(label: string, element: HTMLElement): void {
    if (desktopNavExpanded) return;
    const bounds = element.getBoundingClientRect();
    setRailLabel({ label, top: bounds.top + bounds.height / 2 });
  }

  async function selectBrand(brandId: string): Promise<void> {
    if (brandId === session?.brand.id) {
      setWorkspaceOpen(false);
      return;
    }
    setBrandSwitching(true);
    try {
      await switchBrand(brandId);
      void navigate(location.pathname, { replace: true });
      setWorkspaceOpen(false);
      setContextOpen(false);
    } finally {
      setBrandSwitching(false);
    }
  }

  function navigateWithScope(scope: {
    companyId?: string;
    siteId?: string;
    pointId?: string;
  }): void {
    void navigate(scopedPath(`${location.pathname}${location.search}`, scope));
  }

  function openSearchResult(result: SearchResult): void {
    if (result.deliveryPointId) selectPoint(result.deliveryPointId);
    else if (result.siteId) selectSite(result.siteId);
    else if (result.companyId) selectCompany(result.companyId);
    void navigate(result.path);
  }

  const activeScope = {
    ...(selectedCompany ? { companyId: selectedCompany.id } : {}),
    ...(selectedSite ? { siteId: selectedSite.id } : {}),
    ...(selectedPoint ? { pointId: selectedPoint.id } : {}),
  };
  const activeScopeLink = `${window.location.origin}${scopedPath(
    `${location.pathname}${location.search}`,
    activeScope,
  )}`;

  function toggleDesktopNavigation(): void {
    setDesktopNavExpanded((expanded) => {
      const next = !expanded;
      try {
        window.localStorage.setItem(
          DESKTOP_NAV_KEY,
          next ? "expanded" : "collapsed",
        );
      } catch {
        // The navigation remains usable when browser storage is unavailable.
      }
      return next;
    });
  }

  function renderNavItem(item: NavItem): ReactNode {
    return (
      <NavLink
        key={item.to}
        to={item.to}
        aria-label={item.label}
        aria-current={
          navItemIsActive(
            item,
            location.pathname,
            item.to === location.pathname,
            location.search,
          )
            ? "page"
            : false
        }
        data-label={item.label}
        onMouseEnter={(event) => showRailLabel(item.label, event.currentTarget)}
        onMouseLeave={() => setRailLabel(null)}
        onFocus={(event) => showRailLabel(item.label, event.currentTarget)}
        onBlur={() => setRailLabel(null)}
        onClick={() => {
          setMobileOpen(false);
          setRailLabel(null);
        }}
        className={({ isActive }) =>
          navItemIsActive(item, location.pathname, isActive, location.search)
            ? "rail-link active"
            : "rail-link"
        }
      >
        <item.icon size={19} />
        <span>{item.label}</span>
      </NavLink>
    );
  }

  function renderNavGroup(group: NavGroup): ReactNode {
    const expanded = navigationGroups[group.id] ?? true;
    const children = group.children.filter(
      (child) => role !== null && allowed(child, role),
    );
    const active = children.some((child) =>
      navItemIsActive(
        child,
        location.pathname,
        child.to === location.pathname,
        location.search,
      ),
    );
    return (
      <div key={group.id}>
        <button
          type="button"
          className={`rail-link rail-group-toggle${active ? " active" : ""}`}
          aria-label={group.label}
          aria-expanded={expanded}
          aria-controls={group.id}
          onClick={() =>
            setNavigationGroups((groups) => ({
              ...groups,
              [group.id]: !expanded,
            }))
          }
          onMouseEnter={(event) =>
            showRailLabel(group.label, event.currentTarget)
          }
          onMouseLeave={() => setRailLabel(null)}
          onFocus={(event) => showRailLabel(group.label, event.currentTarget)}
          onBlur={() => setRailLabel(null)}
        >
          <group.icon size={19} />
          <span>{group.label}</span>
          <ChevronDown
            size={16}
            className="rail-group-chevron"
            aria-hidden="true"
          />
        </button>
        <div id={group.id} className="rail-group-links" hidden={!expanded}>
          {children.map(renderNavItem)}
          {group.id === "connector-navigation" && (
            <>
              {connectorsQuery.isPending && (
                <span className="rail-group-message">
                  Chargement des connecteurs…
                </span>
              )}
              {connectorsQuery.isError && (
                <span className="rail-group-message">
                  Liste des connecteurs indisponible
                </span>
              )}
              {connectorsQuery.isSuccess && !connectorsQuery.data.length && (
                <span className="rail-group-message">Aucun connecteur</span>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const administrationNavigation = visibleAdmin.length ? (
    <nav className="rail-nav rail-admin" aria-label="Administration">
      <span className="rail-section-label">Administration</span>
      {visibleAdmin.map((item) =>
        "children" in item ? renderNavGroup(item) : renderNavItem(item),
      )}
    </nav>
  ) : null;
  const mainNavigation = visibleMain.length ? (
    <nav className="rail-nav" aria-label="Navigation principale">
      <span className="rail-section-label">Pilotage</span>
      {visibleMain.map(renderNavItem)}
    </nav>
  ) : null;

  return (
    <div
      className={`app-frame ${desktopNavExpanded ? "nav-expanded" : "nav-collapsed"}${session.user.platformRole === "super_admin" ? " super-admin-frame" : ""}`}
    >
      <aside className={`side-rail ${mobileOpen ? "is-open" : ""}`}>
        <div className="rail-brand">
          <div className="brand-mark">
            <div className="brand-lockup">
              <img
                className="brand-logo-image"
                src={brand.logoLightUrl}
                alt={brand.displayName}
              />
            </div>
          </div>
          <button
            className="rail-toggle"
            aria-label={
              desktopNavExpanded
                ? "Replier le menu principal"
                : "Déplier le menu principal"
            }
            aria-pressed={desktopNavExpanded}
            onClick={toggleDesktopNavigation}
            onMouseEnter={(event) =>
              showRailLabel("Déplier le menu", event.currentTarget)
            }
            onMouseLeave={() => setRailLabel(null)}
            onFocus={(event) =>
              showRailLabel("Déplier le menu", event.currentTarget)
            }
            onBlur={() => setRailLabel(null)}
          >
            {desktopNavExpanded ? (
              <PanelLeftClose size={18} />
            ) : (
              <PanelLeftOpen size={18} />
            )}
          </button>
          <button
            className="mobile-rail-close"
            aria-label="Fermer le menu"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} />
          </button>
        </div>
        {role === "manager" ? (
          <>
            {mainNavigation}
            {administrationNavigation}
          </>
        ) : (
          <>
            {administrationNavigation}
            {mainNavigation}
          </>
        )}
      </aside>

      {!desktopNavExpanded && railLabel ? (
        <div
          className="rail-live-label"
          role="tooltip"
          style={{ top: railLabel.top }}
        >
          {railLabel.label}
        </div>
      ) : null}

      {mobileOpen ? (
        <button
          className="mobile-scrim"
          aria-label="Fermer le menu en dehors du panneau"
          onClick={() => setMobileOpen(false)}
        />
      ) : null}

      <header className="topbar">
        <button
          className="mobile-menu"
          aria-label="Ouvrir le menu"
          onClick={() => setMobileOpen((value) => !value)}
        >
          {mobileOpen ? <X /> : <Menu />}
        </button>
        <div className="workspace-switcher">
          <div
            className="workspace-menu"
            role={accessibleBrands.length > 1 ? "toolbar" : undefined}
            aria-label={
              accessibleBrands.length > 1 ? "Sélection de la marque" : undefined
            }
            onKeyDown={(event) => {
              if (event.key === "Escape") setWorkspaceOpen(false);
            }}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setWorkspaceOpen(false);
              }
            }}
          >
            {accessibleBrands.length > 1 ? (
              <button
                type="button"
                className="workspace-trigger"
                aria-label={`Marque active : ${session.brand.displayName}`}
                aria-haspopup="true"
                aria-expanded={workspaceOpen}
                aria-controls="workspace-options"
                onClick={() => {
                  setProfileOpen(false);
                  setWorkspaceOpen((value) => !value);
                }}
              >
                <span className="workspace-emblem" aria-hidden="true">
                  {session.brand.displayName
                    .split(" ")
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")}
                </span>
                <span className="workspace-current">
                  <strong>{session.brand.displayName}</strong>
                  <small>Marque active</small>
                </span>
                <ChevronDown
                  className={workspaceOpen ? "is-open" : ""}
                  size={15}
                  aria-hidden="true"
                />
              </button>
            ) : (
              <div className="workspace-trigger workspace-trigger-static">
                <span className="workspace-emblem" aria-hidden="true">
                  {session.brand.displayName
                    .split(" ")
                    .slice(0, 2)
                    .map((part) => part[0])
                    .join("")}
                </span>
                <span className="workspace-current">
                  <strong>{session.brand.displayName}</strong>
                  <small>Marque active</small>
                </span>
              </div>
            )}
            {workspaceOpen ? (
              <div
                className="workspace-popover"
                id="workspace-options"
                aria-label="Changer de marque"
              >
                <p>Changer de marque</p>
                {accessibleBrands.map(({ membership, brand: itemBrand }) => {
                  const selected = itemBrand.id === session.brand.id;
                  return (
                    <button
                      type="button"
                      className={`workspace-option ${selected ? "is-selected" : ""}`}
                      key={itemBrand.id}
                      aria-current={selected ? "true" : undefined}
                      onClick={() => void selectBrand(itemBrand.id)}
                    >
                      <span className="workspace-emblem" aria-hidden="true">
                        {itemBrand.displayName
                          .split(" ")
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join("")}
                      </span>
                      <span>
                        <strong>{itemBrand.displayName}</strong>
                        <small>
                          {session.user.platformRole === "super_admin"
                            ? "super admin"
                            : membership.role.replace("_", " ")}
                        </small>
                      </span>
                      {selected ? <Check size={17} aria-hidden="true" /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
        <div
          className="context-switcher"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setContextOpen(false);
            }
          }}
        >
          <button
            className="context-trigger"
            ref={contextTrigger}
            aria-label={`Périmètre actif : ${selectedCompany?.name ?? "toutes les entreprises"}, ${selectedSite?.name ?? "tous les sites"}, ${selectedPoint ? deliveryPointContextLabel(selectedPoint) : "tous les points"}`}
            aria-haspopup="dialog"
            aria-expanded={contextOpen}
            onClick={() => {
              setWorkspaceOpen(false);
              setProfileOpen(false);
              setNotificationsOpen(false);
              setContextOpen((value) => !value);
            }}
          >
            <span
              className={!selectedCompany ? "context-aggregate" : undefined}
            >
              {selectedCompany?.name ?? "Toutes les entreprises"}
            </span>
            <strong className={!selectedSite ? "context-aggregate" : undefined}>
              {selectedSite?.name ?? "Tous les sites"}
            </strong>
            <small className={!selectedPoint ? "context-aggregate" : undefined}>
              {selectedPoint
                ? deliveryPointContextLabel(selectedPoint)
                : "Tous les PDL"}
            </small>
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {contextOpen ? (
            <div
              className="context-popover"
              role="dialog"
              aria-label="Changer le périmètre actif"
            >
              <section>
                <p>Entreprise</p>
                <button
                  className={!selectedCompany ? "is-selected" : ""}
                  aria-pressed={!selectedCompany}
                  onClick={() => {
                    selectCompany("*");
                    navigateWithScope({});
                  }}
                >
                  Toutes les entreprises
                </button>
                {companies.map((company) => (
                  <button
                    key={company.id}
                    className={
                      company.id === selectedCompany?.id ? "is-selected" : ""
                    }
                    onClick={() => {
                      selectCompany(company.id);
                      navigateWithScope({ companyId: company.id });
                    }}
                  >
                    {company.name}
                  </button>
                ))}
              </section>
              <section>
                <p>Site</p>
                <button
                  className={!selectedSite ? "is-selected" : ""}
                  aria-pressed={!selectedSite}
                  onClick={() => {
                    selectCompany(selectedCompany?.id ?? "*");
                    navigateWithScope(
                      selectedCompany ? { companyId: selectedCompany.id } : {},
                    );
                  }}
                >
                  Tous les sites
                </button>
                {sites
                  .filter(
                    (site) =>
                      !selectedCompany || site.companyId === selectedCompany.id,
                  )
                  .map((site) => (
                    <button
                      key={site.id}
                      className={
                        site.id === selectedSite?.id ? "is-selected" : ""
                      }
                      onClick={() => {
                        selectSite(site.id);
                        navigateWithScope({
                          companyId: site.companyId,
                          siteId: site.id,
                        });
                      }}
                    >
                      {site.name}
                    </button>
                  ))}
              </section>
              <section>
                <p>Point de livraison</p>
                {selectedSite ? (
                  <button
                    className={!selectedPoint ? "is-selected" : ""}
                    aria-pressed={!selectedPoint}
                    onClick={() => {
                      selectSite(selectedSite.id);
                      navigateWithScope({
                        companyId: selectedSite.companyId,
                        siteId: selectedSite.id,
                      });
                    }}
                  >
                    Tous les PDL
                  </button>
                ) : (
                  <p>Choisissez un Site pour détailler ses PDL.</p>
                )}
                {deliveryPoints
                  .filter((point) => point.siteId === selectedSite?.id)
                  .map((point) => (
                    <button
                      key={point.id}
                      className={
                        point.id === selectedPoint?.id ? "is-selected" : ""
                      }
                      onClick={() => {
                        selectPoint(point.id);
                        navigateWithScope({
                          ...(selectedCompany
                            ? { companyId: selectedCompany.id }
                            : {}),
                          siteId: point.siteId,
                          pointId: point.id,
                        });
                        setContextOpen(false);
                      }}
                    >
                      {deliveryPointContextLabel(point)}
                    </button>
                  ))}
              </section>
              <footer className="context-popover-actions">
                <CopyValueButton
                  value={activeScopeLink}
                  label="Copier le lien du périmètre"
                />
              </footer>
            </div>
          ) : null}
        </div>
        <div className="top-actions">
          <ContextHelp />
          {role !== "super_admin" ? (
            <GlobalSearch
              brandId={session.brand.id}
              onSelect={openSearchResult}
            />
          ) : null}
          <div
            className="notification-menu"
            role="presentation"
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setNotificationsOpen(false);
                event.currentTarget.querySelector("button")?.focus();
              }
            }}
          >
            <button
              className="icon-button"
              aria-label={`Notifications (${unreadNotifications.length} non lues)`}
              aria-expanded={notificationsOpen}
              onClick={() => {
                setContextOpen(false);
                setProfileOpen(false);
                setNotificationsOpen((value) => !value);
              }}
            >
              <Bell size={18} />
              {unreadNotifications.length ? (
                <span className="notification-dot" aria-hidden="true" />
              ) : null}
              <span className="sr-only">
                {unreadNotifications.length
                  ? `${String(unreadNotifications.length)} notification(s) non lue(s)`
                  : "Aucune notification non lue"}
              </span>
            </button>
            {notificationsOpen ? (
              <div
                className="notification-popover"
                aria-label="Centre de notifications"
              >
                <strong>Notifications</strong>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setNotificationsOpen(false)}
                >
                  Fermer les notifications
                </button>
                {notificationError ? (
                  <p role="alert">{notificationError}</p>
                ) : null}
                {notificationsQuery.isLoading ? (
                  <p role="status">Chargement…</p>
                ) : notificationsQuery.isError ? (
                  <p role="alert">
                    Les notifications sont momentanément indisponibles.
                  </p>
                ) : notifications.length === 0 ? (
                  <p>Aucune notification pour le moment.</p>
                ) : (
                  <ul className="notification-list">
                    {notifications.map((notification) => (
                      <li key={notification.id}>
                        <button
                          type="button"
                          className={
                            notification.readAt ? "is-read" : "is-unread"
                          }
                          onClick={() => void openNotification(notification)}
                        >
                          <strong>{notification.title}</strong>
                          <span>{notification.message}</span>
                          <small>
                            {new Date(notification.createdAt).toLocaleString(
                              "fr-FR",
                            )}
                          </small>
                          {notification.emailState &&
                          notification.emailState !== "disabled" ? (
                            <small>
                              E-mail :{" "}
                              {
                                {
                                  queued: "en attente",
                                  sending: "envoi en cours",
                                  accepted:
                                    "accepté par le fournisseur, livraison non confirmée",
                                  not_sent: "non envoyé",
                                  unknown:
                                    "résultat incertain, aucun renvoi automatique",
                                  cancelled: "annulé",
                                  disabled: "désactivé",
                                }[notification.emailState]
                              }
                            </small>
                          ) : null}
                        </button>
                        <div className="notification-actions">
                          <button
                            type="button"
                            onClick={() =>
                              void updateNotification(notification)
                            }
                          >
                            {notification.readAt
                              ? "Marquer non lue"
                              : "Marquer lue"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              void updateNotification(notification, true)
                            }
                          >
                            Rappeler dans 1 h
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <NotificationPreferences />
              </div>
            ) : null}
          </div>
          <div className="profile-menu">
            <button
              className="profile-trigger"
              onClick={() => {
                setWorkspaceOpen(false);
                setContextOpen(false);
                setNotificationsOpen(false);
                setProfileOpen((value) => !value);
              }}
              aria-expanded={profileOpen}
            >
              <span className="avatar">
                {session.user.firstName[0]}
                {session.user.lastName[0]}
              </span>
              <span className="profile-copy">
                <strong>
                  {session.user.firstName} {session.user.lastName}
                </strong>
                <small>
                  {role.replace("_", " ")}
                  {previewRole ? " · aperçu" : ""}
                </small>
              </span>
              <ChevronDown size={14} />
            </button>
            {profileOpen ? (
              <div className="profile-popover">
                <NavLink to="/app/profile">
                  <CircleUserRound size={16} /> Mon profil
                </NavLink>
                {session.user.platformRole === "super_admin" ? (
                  previewRole ? (
                    <button onClick={leavePreview}>
                      <EyeOff size={16} /> Revenir à la vue super admin
                    </button>
                  ) : (
                    <button onClick={enterPreview}>
                      <Eye size={16} /> Voir comme un Energy Manager
                    </button>
                  )
                ) : null}
                <button onClick={() => void handleLogout()}>
                  <LogOut size={16} /> Se deconnecter
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <main className="app-main">
        {previewRole ? (
          <div className="preview-mode-bar" role="status">
            <Eye size={14} aria-hidden="true" />
            <strong>Aperçu Energy Manager</strong>
            <span>
              L’interface se présente comme pour un Energy Manager · vous restez
              connecté en super admin, aucune identité n’est empruntée
            </span>
            <button type="button" onClick={leavePreview}>
              Quitter l’aperçu
            </button>
          </div>
        ) : null}
        {scopeMessage ? (
          <div className="app-context-notice" role="alert">
            {scopeMessage}
          </div>
        ) : null}
        {safeInternalReturnTo(
          new URLSearchParams(location.search).get("returnTo"),
        ) ? (
          <div className="app-context-notice" role="status">
            <span>Vous corrigez une donnée depuis une autre vue.</span>
            <button
              type="button"
              className="secondary-button"
              onClick={() => {
                const returnTo = safeInternalReturnTo(
                  new URLSearchParams(location.search).get("returnTo"),
                );
                if (returnTo) void navigate(returnTo);
              }}
            >
              Revenir à la vue d’origine
            </button>
          </div>
        ) : null}
        {onlySyntheticSeries ? (
          <div className="demo-mode-bar" role="status">
            <Database size={14} aria-hidden="true" />
            <strong>Jeu de démonstration</strong>
            <span>
              Toutes les séries de mesure de cette marque sont synthétiques ·
              aucun relevé réel n’a encore été synchronisé
            </span>
          </div>
        ) : null}
        {scopeMessage ? (
          <section className="content-page app-scope-refusal">
            <p className="eyebrow">PÉRIMÈTRE REFUSÉ</p>
            <h1>Ce lien ne peut pas être affiché</h1>
            <p>
              Les données de la vue demandée restent masquées. Choisissez
              explicitement un périmètre autorisé pour continuer.
            </p>
            <button
              type="button"
              className="primary-button"
              onClick={() => void navigate("/app/portfolio", { replace: true })}
            >
              Retour au portefeuille autorisé
            </button>
          </section>
        ) : (
          children
        )}
      </main>

      <nav className="mobile-bottom-nav" aria-label="Navigation mobile">
        {visibleMain.slice(0, 4).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            aria-current={
              navItemIsActive(item, location.pathname, false)
                ? "page"
                : undefined
            }
            className={({ isActive }) =>
              navItemIsActive(item, location.pathname, isActive) ? "active" : ""
            }
          >
            <item.icon size={20} />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
